import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useZCalendarWebApiHandler_disableTouringForListing,
  useZCalendarWebApiHandler_enableTouringForListing,
  useZCalendarWebApiHandler_getAvailabilityAndToursForListing,
  useZCalendarWebApiHandler_getTourConfigAndAvailability,
  useZCalendarWebApiHandler_performTourAction,
  useZCalendarWebApiHandler_removeAvailability,
  useZCalendarWebApiHandler_setAvailabilitiesLite,
  useZCalendarWebApiHandler_setAvailability,
  useZCalendarWebApiHandler_storeListingCalendarTimeConfig,
  useZCalendarWebApiHandler_storeListingTourDuration,
  useZCalendarWebApiHandler_updateAvailability,
} from '@zg-rentals/ts-z-calendar-client-react-query';
import {
  type ActionMetadataEntryPoint,
  type GetAvailabilityAndToursForListingWebApiResponseDto,
  type GetTourConfigAndAvailabilityWebApiResponseDto,
  type LocalTime,
  type SetAvailabilityLiteDto,
  type TourActionParams,
  TourActionType,
  type TourConfirmationType,
  TourDuration,
  TourStatusForZcheduler,
  TourStatusForZcheduler_values,
  TourType,
  ZCalendarWebApiHandler_getAvailabilityAndToursForListing_Path,
  ZCalendarWebApiHandler_getTourConfigAndAvailability_Path,
  ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path,
} from '@zg-rentals/ts-z-calendar-client';
import { addWeeks } from 'date-fns';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useToast } from '@zillow/constellation-10';
import UnmaskedToast from '../components/UnmaskedToast';
import { logError } from '@zg-rentals/log-error';

import {
  type AvailabilityEvent,
  DEFAULT_CALENDAR_END_TIME,
  DEFAULT_CALENDAR_START_TIME,
  type TimeZoneIds,
  TOUR_CALENDAR_MODES,
  type TourEvent,
} from '../constants/TourCalendar';
import {
  createEventsFromAvailabilityResponse,
  createEventsFromBookedTourResponse,
  createSetAvailabilityPostDataFromEvent,
  createUpdateAvailabilityPostDataFromEvent,
  createUpdatedTourEvents,
} from './tourCalendarWebApiBuilder';
import { convertDateToLocalDate, getEventToastDisplayDate } from './formatTime';
import { useErrorHandler } from '../hooks/useErrorHandler';

const FILTERED_TOUR_STATUSES = TourStatusForZcheduler_values.filter(
  (status) => status !== TourStatusForZcheduler.CANCELLED && status !== TourStatusForZcheduler.DECLINED,
);

// Same set plus CANCELLED, so cancelled tours persist across calendar reloads. The backend already
// returns cancelled tours, so requesting this status is all that's needed. DECLINED stays excluded.
const PERSISTED_TOUR_STATUSES = TourStatusForZcheduler_values.filter(
  (status) => status !== TourStatusForZcheduler.DECLINED,
);

// Cancelled tours are opt-in per consuming app (feature-flagged in ZRM); default keeps them hidden.
export const getCalendarTourStatuses = (showCancelledTours: boolean) =>
  showCancelledTours ? PERSISTED_TOUR_STATUSES : FILTERED_TOUR_STATUSES;

const getTourActionTypeDisplayString = (tourActionType: TourActionType): string => {
  switch (tourActionType) {
    case TourActionType.IRENE_CONFIRM:
      return 'confirmed';
    case TourActionType.IRENE_DECLINE:
      return 'declined';
    case TourActionType.IRENE_CANCEL:
      return 'canceled';
  }

  return '';
};

type UseTourCalendarApiArgs = {
  enableQueries?: boolean;
  lastActivatedAt?: number;
  listingAlias: string;
  mode: TOUR_CALENDAR_MODES;
  onGetTourConfigAndAvailabilitySuccess?: (data: GetTourConfigAndAvailabilityWebApiResponseDto) => void;
  onTouringToggleSuccess?: (isDisabling?: boolean) => void;
  showCancelledTours?: boolean;
};

/**
 * Custom hook for encapsulating tour calendar API interactions and state from the backend
 * It is intentional that state values (e.g. availabilityEvents, tourEvents, tourConfirmationType, timeZone) are only
 * updated in this hook. This allows us to know when state values are set in the backend. See `TourAvailabilityWizard` for example
 */
const useTourCalendarApi = ({
  enableQueries = true,
  lastActivatedAt,
  listingAlias,
  mode,
  onGetTourConfigAndAvailabilitySuccess: onGetTourConfigAndAvailabilitySuccessCallback,
  onTouringToggleSuccess: onTouringToggleSuccessCallback,
  showCancelledTours = false,
}: UseTourCalendarApiArgs) => {
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const [isTourConfigLoading, setIsTourConfigLoading] = useState(true);
  const [availabilityEvents, setAvailabilityEvents] = useState<Array<AvailabilityEvent>>([]);
  const [tourEvents, setTourEvents] = useState<Array<TourEvent>>([]);
  const [tourConfirmationType, setTourConfirmationType] = useState<TourConfirmationType>();
  const [timeZone, setTimeZone] = useState<TimeZoneIds>();
  const [calendarStartTime, setCalendarStartTime] = useState<LocalTime>(DEFAULT_CALENDAR_START_TIME);
  const [calendarEndTime, setCalendarEndTime] = useState<LocalTime>(DEFAULT_CALENDAR_END_TIME);
  const [tourDuration, setTourDuration] = useState<TourDuration>(TourDuration.MINUTES_30);
  const [initialZoneIdWithoutTourConfig, setInitialZoneIdWithoutTourConfig] = useState<TimeZoneIds>();

  const refetchEndpointByPath = useCallback(
    (path: string) => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === path,
      });
    },
    [queryClient],
  );

  const handleGetAvailabilityAndTourConfigSuccess = useCallback(
    (data: GetTourConfigAndAvailabilityWebApiResponseDto) => {
      onGetTourConfigAndAvailabilitySuccessCallback?.(data);

      const availabilityEventsFromRes = createEventsFromAvailabilityResponse(data);
      const filteredAvailabilityEventsFromRes = availabilityEventsFromRes.filter((event) => !event.isPastEvent);
      setAvailabilityEvents(filteredAvailabilityEventsFromRes);

      if ('tourConfig' in data && data.tourConfig) {
        const confirmationType = data.tourConfig.tourSchedulingTypes.schedulingTypes[0].confirmationType;
        const zoneId = data.tourConfig.zoneId;
        const calendarStart = data.tourConfig.calendarStartTime;
        const calendarEnd = data.tourConfig.calendarEndTime;
        const tourDuration = data.tourConfig.tourDuration;

        setTourConfirmationType(confirmationType);
        setTimeZone(zoneId as TimeZoneIds);
        setCalendarStartTime(calendarStart);
        setCalendarEndTime(calendarEnd);
        setTourDuration(tourDuration);
      }

      if ('zoneIdWithoutTourConfig' in data && data.zoneIdWithoutTourConfig) {
        // Store initial zoneIdWithoutTourConfig to preserve timezone step skip behavior during relisting
        //
        // Issue: Once touring is enabled, zoneIdWithoutTourConfig is removed from API response,
        // but we still need to know if timezone step should be skipped for the remainder of this session
        setInitialZoneIdWithoutTourConfig(data.zoneIdWithoutTourConfig as TimeZoneIds);
      }

      setIsTourConfigLoading(false);
    },
    [onGetTourConfigAndAvailabilitySuccessCallback],
  );

  const onGetTourConfigAndAvailabilitySuccess = useCallback(
    (data: GetTourConfigAndAvailabilityWebApiResponseDto) => {
      handleGetAvailabilityAndTourConfigSuccess(data);
    },
    [handleGetAvailabilityAndTourConfigSuccess],
  );

  const onGetAvailabilityAndToursForListingSuccess = useCallback(
    (data: GetAvailabilityAndToursForListingWebApiResponseDto) => {
      handleGetAvailabilityAndTourConfigSuccess(data);

      if ('tours' in data && data.tours) {
        const bookedTourEventsFromRes = createEventsFromBookedTourResponse(data);
        setTourEvents(bookedTourEventsFromRes);
      }
    },
    [handleGetAvailabilityAndTourConfigSuccess],
  );

  const refetchTouringData = useCallback(() => {
    refetchEndpointByPath(ZCalendarWebApiHandler_getAvailabilityAndToursForListing_Path);
    refetchEndpointByPath(ZCalendarWebApiHandler_getTourConfigAndAvailability_Path);
  }, [refetchEndpointByPath]);

  const {
    isLoading: isGetTourConfigAndAvailabilityLoading,
    data: tourConfigAndAvailabilityRes,
    isSuccess: isGetTourConfigAndAvailabilitySuccess,
  } = useZCalendarWebApiHandler_getTourConfigAndAvailability(
    {
      fromDate: convertDateToLocalDate(new Date()),
      toDate: convertDateToLocalDate(addWeeks(new Date(), 3)),
      listingAlias,
    },
    {
      enabled: enableQueries && mode === TOUR_CALENDAR_MODES.TOUR_AVAILABILITY,
    },
  );

  useEffect(() => {
    if (isGetTourConfigAndAvailabilitySuccess && tourConfigAndAvailabilityRes) {
      onGetTourConfigAndAvailabilitySuccess(tourConfigAndAvailabilityRes);
    }
  }, [isGetTourConfigAndAvailabilitySuccess, tourConfigAndAvailabilityRes, onGetTourConfigAndAvailabilitySuccess]);

  const {
    data: availabilityAndToursForListingRes,
    isFetching: isGetAvailabilityAndToursForListingFetching,
    isLoading: isGetAvailabilityAndToursForListingLoading,
    isSuccess: isGetAvailabilityAndToursForListingSuccess,
  } = useZCalendarWebApiHandler_getAvailabilityAndToursForListing(
    {
      listingAlias,
      from: convertDateToLocalDate(lastActivatedAt ? new Date(lastActivatedAt) : new Date()),
      to: convertDateToLocalDate(addWeeks(new Date(), 3)),
      tourStatuses: getCalendarTourStatuses(showCancelledTours),
    },
    {
      enabled: enableQueries && mode === TOUR_CALENDAR_MODES.FULL_CALENDAR,
    },
  );

  useEffect(() => {
    if (
      isGetAvailabilityAndToursForListingSuccess &&
      availabilityAndToursForListingRes &&
      !isGetAvailabilityAndToursForListingFetching
    ) {
      onGetAvailabilityAndToursForListingSuccess(availabilityAndToursForListingRes);
    }
  }, [
    isGetAvailabilityAndToursForListingFetching,
    isGetAvailabilityAndToursForListingSuccess,
    availabilityAndToursForListingRes,
    onGetAvailabilityAndToursForListingSuccess,
  ]);

  const {
    mutateAsync: setAvailabilityMutateAsync,
    isError: isSetAvailabilityError,
    error: setAvailabilityError,
  } = useZCalendarWebApiHandler_setAvailability({ onSuccess: refetchTouringData });
  useErrorHandler({ isError: isSetAvailabilityError, error: setAvailabilityError });

  const {
    mutateAsync: updateAvailabilityMutateAsync,
    isError: isUpdateAvailabilityError,
    error: updateAvailabilityError,
  } = useZCalendarWebApiHandler_updateAvailability({
    onSuccess: refetchTouringData,
  });
  useErrorHandler({ isError: isUpdateAvailabilityError, error: updateAvailabilityError });

  const {
    mutateAsync: removeAvailabilityMutateAsync,
    isError: isRemoveAvailabilityError,
    error: removeAvailabilityError,
  } = useZCalendarWebApiHandler_removeAvailability({
    onSuccess: refetchTouringData,
  });
  useErrorHandler({ isError: isRemoveAvailabilityError, error: removeAvailabilityError });

  const { mutateAsync: performTourActionMutateAsync } = useZCalendarWebApiHandler_performTourAction({
    onSuccess: (data) => {
      setTourEvents((existingTourEvents) => createUpdatedTourEvents(existingTourEvents, data));
    },
  });

  const {
    mutateAsync: setAvailabilitiesLiteMutate,
    isPending: isSetAvailabilitiesLiteLoading,
    isSuccess: isSetAvailabilitiesLiteSuccess,
  } = useZCalendarWebApiHandler_setAvailabilitiesLite();

  useEffect(() => {
    if (isSetAvailabilitiesLiteSuccess) {
      refetchTouringData();
    }
  }, [isSetAvailabilitiesLiteSuccess, refetchTouringData]);

  const {
    mutateAsync: enableTouringForListingMutateAsync,
    data: enableTouringForListingRes,
    isPending: isEnableTouringForListingLoading,
    isSuccess: isEnableTouringForListingSuccess,
  } = useZCalendarWebApiHandler_enableTouringForListing();

  useEffect(() => {
    if (isEnableTouringForListingSuccess && enableTouringForListingRes) {
      const confirmationType = enableTouringForListingRes.enabledTourTypes[0].confirmationType;
      const zoneId = enableTouringForListingRes.zoneId;
      setTourConfirmationType(confirmationType);
      setTimeZone(zoneId as TimeZoneIds);
      setTourDuration(enableTouringForListingRes.tourDuration);

      refetchTouringData();
      onTouringToggleSuccessCallback?.();
    }
  }, [isEnableTouringForListingSuccess, enableTouringForListingRes, refetchTouringData]);

  const {
    mutateAsync: disableTouringForListingMutateAsync,
    isPending: isDisableTouringForListingLoading,
    isSuccess: isDisableTouringForListingSuccess,
  } = useZCalendarWebApiHandler_disableTouringForListing();

  useEffect(() => {
    if (isDisableTouringForListingSuccess) {
      setTourConfirmationType(undefined);
      setTimeZone(undefined);

      // if user disabled touring while on the tour calendar, they will be redirected to property overview,
      // so need to refetch tour treatment data to show entrypoints correctly
      refetchEndpointByPath(ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path);
      if (mode === TOUR_CALENDAR_MODES.TOUR_AVAILABILITY) {
        // Need to invalidate tour data cache because react query will trigger handleGetAvailabilityAndTourConfigSuccess with stale data
        refetchTouringData();
      }
      onTouringToggleSuccessCallback?.(true);
    }
  }, [isDisableTouringForListingSuccess, mode, refetchTouringData, refetchEndpointByPath]);

  const { mutateAsync: storeListingCalendarTimeConfigMutateAsync } =
    useZCalendarWebApiHandler_storeListingCalendarTimeConfig();

  const { mutateAsync: storeListingTourDurationMutateAsync, isSuccess: isStoreListingTourDurationSuccess } =
    useZCalendarWebApiHandler_storeListingTourDuration();

  useEffect(() => {
    if (isStoreListingTourDurationSuccess) {
      refetchTouringData();
    }
  }, [isStoreListingTourDurationSuccess, refetchTouringData]);

  const addAvailabilityEvent = useCallback(
    (availabilityEvent: AvailabilityEvent) => {
      if (!timeZone) {
        logError({
          error: new Error('No timeZone set when adding availability event'),
          context: {
            listingAlias,
          },
        });

        return Promise.reject('No timeZone set when adding availability event');
      }

      const setAvailabilityPostData = createSetAvailabilityPostDataFromEvent({
        event: availabilityEvent,
        listingAlias,
      });

      return setAvailabilityMutateAsync({
        postData: setAvailabilityPostData,
      });
    },
    [setAvailabilityMutateAsync, listingAlias, timeZone],
  );

  const updateAvailabilityEvent = useCallback(
    (availabilityEvent: AvailabilityEvent) => {
      if (!timeZone) {
        logError({
          error: new Error('No timeZone set when updating availability event'),
          context: {
            listingAlias,
          },
        });

        return Promise.reject('No timeZone set when updating availability event');
      }

      const prevAvailabilityEvents = [...availabilityEvents];

      // replace availability event in state to prevent event from flickering back to original position
      // Ex: https://zillowgroup.enterprise.slack.com/files/U18947XPX/F08DXDZN2M8/screen_recording_2025-02-18_at_2.47.33___pm.mov
      setAvailabilityEvents((availabilityEvents) => {
        const filtered = availabilityEvents.filter((ev) => ev.id !== availabilityEvent.id);
        const newAvailabilityEventToTimeZone = {
          ...availabilityEvent,
          start: availabilityEvent.start,
          end: availabilityEvent.end,
        };
        return [...filtered, newAvailabilityEventToTimeZone];
      });

      return updateAvailabilityMutateAsync({
        postData: createUpdateAvailabilityPostDataFromEvent({
          event: availabilityEvent,
          listingAlias,
        }),
      }).catch((error) => {
        setAvailabilityEvents(prevAvailabilityEvents);
        throw error;
      });
    },
    [updateAvailabilityMutateAsync, listingAlias, timeZone],
  );

  const removeAvailabilityEvent = useCallback(
    (eventId: string) => {
      return removeAvailabilityMutateAsync({
        postData: {
          listingAlias,
          tourAvailabilityId: eventId,
        },
      });
    },
    [removeAvailabilityMutateAsync, listingAlias],
  );

  const setAvailabilitiesLite = useCallback(
    (setAvailabilityLiteDto: Array<SetAvailabilityLiteDto>) => {
      return setAvailabilitiesLiteMutate({
        postData: {
          listingAlias: listingAlias,
          availabilities: setAvailabilityLiteDto,
        },
      });
    },
    [setAvailabilitiesLiteMutate, listingAlias],
  );

  const performTourAction = useCallback(
    (tourEvent: TourEvent, actionType: TourActionType, tourActionParams?: TourActionParams) => {
      return performTourActionMutateAsync(
        {
          postData: {
            listingAlias,
            tourId: tourEvent.id,
            actionType,
            tourActionParams,
          },
        },
        {
          onSuccess: (data, variables) => {
            setTourEvents((existingTourEvents) => createUpdatedTourEvents(existingTourEvents, data));

            const tourActionType = variables.postData.actionType;
            const tourActionTypeString = getTourActionTypeDisplayString(tourActionType);

            enqueueToast(
              <UnmaskedToast tone="success">
                The tour request for {getEventToastDisplayDate(tourEvent)} was {tourActionTypeString}.
              </UnmaskedToast>,
            );
          },
          onError: (_, variables) => {
            const tourActionType = variables.postData.actionType;
            const tourActionTypeString = getTourActionTypeDisplayString(tourActionType);

            enqueueToast(
              <UnmaskedToast tone="critical">
                The tour couldn&apos;t be {tourActionTypeString}. Try again in a few moments.
              </UnmaskedToast>,
            );
          },
        },
      );
    },
    [performTourActionMutateAsync, listingAlias, enqueueToast],
  );

  const enableTouringForListing = useCallback(
    (
      confirmationType: TourConfirmationType,
      selectedTimeZone?: TimeZoneIds,
      selectedTourDuration?: TourDuration,
      entryPoint?: ActionMetadataEntryPoint,
    ) => {
      if (!selectedTimeZone) {
        logError({
          error: new Error('No timeZone set when enabling touring for listing'),
          context: {
            listingAlias,
          },
        });
        return Promise.reject('No timeZone set when enabling touring for listing');
      }
      // Use selectedTourDuration parameter if provided, otherwise fall back to hook's tourDuration state
      const durationToUse = selectedTourDuration ?? tourDuration;
      return enableTouringForListingMutateAsync({
        postData: {
          enabledTourTypes: [
            {
              tourType: TourType.IN_PERSON,
              confirmationType: confirmationType,
            },
          ],
          listingAlias,
          zoneId: selectedTimeZone,
          tourDuration: durationToUse,
          entryPoint,
        },
      });
    },
    [enableTouringForListingMutateAsync, listingAlias, tourDuration],
  );

  const disableTouringForListing = useCallback(() => {
    return disableTouringForListingMutateAsync({
      postData: {
        listingAlias,
      },
    });
  }, [disableTouringForListingMutateAsync, listingAlias]);

  const storeListingCalendarTimeConfig = useCallback(
    (startTime: LocalTime, endTime: LocalTime) => {
      return storeListingCalendarTimeConfigMutateAsync(
        {
          postData: {
            listingAlias,
            calendarStartTime: startTime,
            calendarEndTime: endTime,
          },
        },
        {
          onSuccess: (_, variables) => {
            setCalendarStartTime(variables.postData.calendarStartTime);
            setCalendarEndTime(variables.postData.calendarEndTime);
          },
        },
      );
    },
    [storeListingCalendarTimeConfigMutateAsync, listingAlias],
  );

  const storeListingTourDuration = useCallback(
    (duration: TourDuration) => {
      return storeListingTourDurationMutateAsync(
        {
          postData: {
            listingAlias,
            tourDuration: duration,
          },
        },
        {
          onSuccess: (_, variables) => {
            setTourDuration(variables.postData.tourDuration);
          },
        },
      );
    },
    [storeListingTourDurationMutateAsync, listingAlias],
  );

  return useMemo(
    () => ({
      // State
      availabilityEvents,
      tourEvents,
      tourConfirmationType,
      timeZone,
      isTouringSchedulingEnabled: Boolean(tourConfirmationType && timeZone),
      calendarStartTime,
      calendarEndTime,
      isTourConfigLoading,
      tourDuration,
      initialZoneIdWithoutTourConfig,
      // State functions
      refetchTouringData,
      // Mutations
      addAvailabilityEvent,
      updateAvailabilityEvent,
      removeAvailabilityEvent,
      performTourAction,
      enableTouringForListing,
      disableTouringForListing,
      storeListingCalendarTimeConfig,
      storeListingTourDuration,
      setAvailabilitiesLite,
      // Request Statuses
      isGetTourConfigAndAvailabilityLoading:
        isGetAvailabilityAndToursForListingLoading || isGetTourConfigAndAvailabilityLoading,
      isEnableTouringForListingLoading,
      isDisableTouringForListingLoading,
      isSetAvailabilitiesLiteLoading,
    }),
    [
      availabilityEvents,
      tourEvents,
      tourConfirmationType,
      timeZone,
      calendarStartTime,
      calendarEndTime,
      isTourConfigLoading,
      tourDuration,
      refetchTouringData,
      addAvailabilityEvent,
      updateAvailabilityEvent,
      removeAvailabilityEvent,
      performTourAction,
      enableTouringForListing,
      disableTouringForListing,
      storeListingCalendarTimeConfig,
      storeListingTourDuration,
      setAvailabilitiesLite,
      isGetTourConfigAndAvailabilityLoading,
      isEnableTouringForListingLoading,
      isDisableTouringForListingLoading,
      isSetAvailabilitiesLiteLoading,
      isGetAvailabilityAndToursForListingLoading,
      initialZoneIdWithoutTourConfig,
    ],
  );
};

export default useTourCalendarApi;
